import { useTranslation } from "react-i18next";
import { CreativeUsageWarning } from "@/components/creative-materials/CreativeUsageWarning";
import {
  canAllowExternalUse,
  creativeUsageWarningLevel,
} from "@/components/creative-materials/creativeUsage";
import { PrivateImage } from "@/pages/KnowledgeHubPage/creatives/PrivateImage";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import type { CreativeMainReferenceProps } from "@/types/creative-library";

export function CreativeMainReference({
  subject,
  items,
  pinnedTokens,
  selection,
  disabled,
  onChange,
}: CreativeMainReferenceProps) {
  const { t } = useTranslation("app");
  const primaryToken = subject?.primaryReferenceToken;
  const primaryImage = items.find((item) => item.token === primaryToken);
  const pinnedReferences = pinnedTokens;
  const selectedAndPinned = new Set([
    ...(selection.referenceTokens ?? []),
    ...pinnedReferences,
  ]);
  const mainSelected = Boolean(
    primaryToken && selectedAndPinned.has(primaryToken),
  );
  const mainUnavailable = Boolean(
    subject?.primaryReferenceUnavailable ||
    (primaryToken &&
      (!subject?.referenceTokens.includes(primaryToken) || !primaryImage)),
  );
  const mainUsageWarning =
    selection.intendedUse === "external" && primaryImage && !mainUnavailable
      ? creativeUsageWarningLevel(primaryImage)
      : null;
  const mainFull = !mainSelected && selectedAndPinned.size >= 4;
  const mainIssue = mainUnavailable
    ? "mainReferenceUnavailable"
    : mainFull
      ? "mainReferenceFull"
      : null;
  function useMainReference() {
    if (!primaryToken || mainIssue || mainSelected || disabled) return;
    // Add explicitly; never replace current references or infer conditional Brand admission here.
    onChange([...(selection.referenceTokens ?? []), primaryToken]);
  }

  const manualIdentityPins = (selection.referenceTokens ?? []).filter((token) =>
    subject?.referenceTokens.includes(token),
  );
  const identityPins = [
    ...manualIdentityPins,
    ...pinnedTokens.filter((token) => subject?.referenceTokens.includes(token)),
  ];
  return (
    <>
      {subject && (
        <div
          className="flex flex-wrap items-center justify-between gap-2"
          data-testid="thread-character-angle-mode"
        >
          <Text size="xs" color="muted">
            {t(
              identityPins.length
                ? "creativeLibrary.characterAnglesPinned"
                : "creativeLibrary.characterAnglesAuto",
            )}
          </Text>
          {manualIdentityPins.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() =>
                onChange(
                  (selection.referenceTokens ?? []).filter(
                    (token) => !identityPins.includes(token),
                  ),
                )
              }
              data-testid="thread-character-auto"
            >
              {t("creativeLibrary.chooseAnglesAuto")}
            </Button>
          )}
        </div>
      )}
      {subject && (primaryToken || subject.primaryReferenceUnavailable) && (
        <div
          className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-background p-3"
          data-testid="thread-main-reference"
        >
          <div className="flex min-w-0 items-center gap-3">
            <PrivateImage
              source={mainUnavailable ? undefined : primaryImage?.mediaUrl}
              alt={primaryImage?.title ?? subject.name}
              className="h-12 w-12 shrink-0 rounded-md object-contain"
            />
            <div className="min-w-0 flex-1">
              <Text size="xs" color="muted">
                {t("creativeLibrary.mainReference")}
              </Text>
              <Text size="sm" className="truncate">
                {mainUnavailable
                  ? t("creativeLibrary.unavailableSelection")
                  : primaryImage?.title}
              </Text>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            disabled={
              disabled || Boolean(mainIssue) || mainSelected || !primaryToken
            }
            onClick={useMainReference}
            data-testid="thread-use-main-reference"
          >
            {t(
              mainSelected
                ? "creativeLibrary.mainReferenceSelected"
                : "creativeLibrary.useMainReference",
            )}
          </Button>
          {mainIssue && (
            <Text size="xs" color="muted" role="status">
              {t(`creativeLibrary.${mainIssue}`)}
            </Text>
          )}
          {mainUsageWarning && primaryImage && (
            <CreativeUsageWarning
              name={primaryImage.title}
              usage={mainUsageWarning}
              allowedUseToken={
                canAllowExternalUse(primaryImage) ? primaryImage.token : null
              }
              testId="thread-main-reference-usage"
            />
          )}
        </div>
      )}
    </>
  );
}
